<template>
	<v-app id="app">
		<NetworkMessages ref="messages" />
		<Navigation @show-auth="showAuthentication = true" />

		<div v-if="!isAllowedPage && !isAuthenticated" class="lockdown-message">
			<div class="lockdown-message__content">
				<div class="lockdown-message__logo-row">
					<img src="@/assets/primar_2k2_icon.png" alt="PRIMAR Icon" class="lockdown-message__icon" />
					<img src="@/assets/company_logo.png" alt="PRIMAR" class="lockdown-message__logo" />
				</div>
				Welcome to the PRIMAR Member Area. To access the content, please click the padlock and enter your login details.
			</div>
		</div>

		<v-main v-show="isAllowedPage">
			<v-container class="pt-16" fill-height>
				<h1 class="text-h2 mb-8" v-if="showPageTitle" v-text="pageTitle" />

				<v-row>
					<v-col>
						<router-view id="view" :key="$route.fullPath" />
					</v-col>
				</v-row>
			</v-container>
		</v-main>

		<LoadingAnimation :show="showLoading" />
		<AmplifyAuthentication v-if="showAuthentication" @close="showAuthentication = false" />

		<Footer :contactInfo="contactInfo" />
	</v-app>
</template>

<script>
import routes from '@/router/routes'

import Navigation from './components/base/AppNavigation'
import Footer from './components/base/AppFooter'

import LoadingAnimation from './components/base/AppLoading'

import { onAuthUIStateChange } from '@aws-amplify/ui-components'
import { mapGetters } from 'vuex'
import AmplifyAuthentication from './components/dialogs/AmplifyAuthentication'

import bus from '@/eventbus'

export default {
	name: 'App',
	routes,
	components: {
		Navigation,
		Footer,
		LoadingAnimation,
		AmplifyAuthentication,
		// Components imports on demand:
		NetworkMessages: () => import('@/components/admin/NetworkMessages'),
	},

	metaInfo: {
      title: 'PRIMAR',
      titleTemplate: '%s | PRIMAR'
  },

	data: () => ({
		showAuthentication: false,
		showLoading: false,
		contactInfo: {}
	}),

	created() {
		// Listen to the global event bus
		bus
			.$on('loading-show', this.showLoad)
			.$on('loading-close', this.closeLoad)
			.$on('auth-show', this.showAuth)
			.$on('auth-close', this.closeAuth)
			.$on('show-message', this.showMessage)
			.$on('router-go', function (path) {
				this.$router.push(path)
			})

		// Init account storage, checks for existing login session
		this.$store.dispatch('init')

		// Amplify Authentication dialog event handler
		onAuthUIStateChange((authState, authData) => {
			this.$store.dispatch('handleAuthChange', { authState, authData })
		})
	},

	// DEBUGGING
	watch: {
		responsiveSize(value) {
			console.info('RESPONSIVE CHANGE', value)
		},

		$route: {
			immediate: true,
			handler(to) {
				if (to.path === '/' && !this.isAuthenticated) {
					this.showAuthentication = true
				}
			}
		}
	},

	computed: {
		...mapGetters(['isAuthenticated']),

		showPageTitle() {
			return this.$route.name != 'Home'
		},

		pageTitle() {
			return this.$route.meta.title
		},

		isAllowedPage() {
			const allowed = ['/admin/users', '/documents/PAC', '/documents/TEWG']
			return allowed.includes(this.$route.path)
		}
	},

	methods: {
		showLoad() {
			this.showLoading = true
		},
		closeLoad() {
			this.showLoading = false
		},
		showAuth() {
			this.showAuthentication = true
		},
		closeAuth() {
			this.showAuthentication = false
		},
		showMessage(message) {
			this.$refs.messages.showMessage(message)
		}
	}
}
</script>

<style lang="stylus">
.v-application {
	background: url('/img/wavesBg.jpg') !important;
	background-position: center !important;
	background-size: cover !important;
	background-attachment: fixed !important;
	background-repeat: no-repeat !important;
}

.leftOffset {
	margin-left: 320px;
}

html, body {
	height: 100%;
}

.opaqueBg {
	background-color: rgba(255, 255, 255, 0.9) !important;
}

.opaqueBg02 {
	background-color: rgba(255, 255, 255, 0.2) !important;
}

.opaquerBg {
	background-color: rgba(255, 255, 255, 0.6) !important;
}

.opaqueBlueBg {
	background-color: rgba(0, 56, 122, 0.8) !important;
}

.opaquerBlueBg {
	background-color: rgba(0, 56, 122, 0.5) !important;
}

.opaque {
	opacity: 0.9 !important;
}

.opaquer {
	opacity: 0.6 !important;
}

.linearGradient {
	background: linear-gradient(180deg, rgba(0, 56, 122, 0) 50%, rgba(255, 255, 255, 0.5) 100%);
}

/* Lockdown message */
.lockdown-message {
	position: fixed !important;
	top: 0;
	left: 0;
	width: 100vw;
	height: 100vh;
	display: flex !important;
	align-items: center;
	justify-content: center;
}

.lockdown-message__content {
	font-size: 2rem;
	font-weight: 500;
	color: rgba(255, 255, 255, 0.95);
	text-align: center;
	padding: 48px 64px;
	background-color: rgba(0, 56, 122, 0.7);
	border-radius: 12px;
	max-width: 700px;
	line-height: 1.5;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 32px;
}

.lockdown-message__logo-row {
	display: flex;
	align-items: center;
	gap: 16px;
}

.lockdown-message__icon {
	width: 80px;
	object-fit: contain;
}

.lockdown-message__logo {
	width: 280px;
	object-fit: contain;
}

/* === SITE LOCKDOWN: hide all content except logo, login lock, and internal nav buttons === */
footer.v-footer {
	display: none !important;
}

/* Hide public page navigation tabs (but keep extension row for username chip) */
.v-app-bar .v-tabs,
.v-toolbar__extension .v-tabs {
	display: none !important;
}
.mobile-nav-list {
	display: none !important;
}

/* Hide admin mode switcher and SHOW panel */
.v-input--switch,
.switcher {
	display: none !important;
}
</style>